import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import type { FunctionalFormFieldProps } from '@adornis/formfield/functional-form-field.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing, type TemplateResult } from 'lit';
import { searchCompanies } from '../../api/searchCompanies.js';
import { searchProducts } from '../../api/searchProducts.js';
import type { Company } from '../../db/Company.js';
import type { Product } from '../../db/Product.js';
import type { SapFieldIssue } from '../../features/export/db/SapValidation.js';
import { referenceKindOf, type ReferenceKind } from '../../features/notifications/answer-reference.js';
import { answerNotification } from '../../features/notifications/api/answerNotification.js';
import { answerRequestNotifications } from '../../features/notifications/api/answerRequestNotifications.js';
import { confirmAnswerInterpretation } from '../../features/notifications/api/confirmAnswerInterpretation.js';
import { subscribeRequestNotifications } from '../../features/notifications/api/subscribeRequestNotifications.js';
import {
  NotificationAnswerDraft,
  NotificationCategory,
  NotificationStatus,
  type UserNotification,
} from '../../features/notifications/db/UserNotification.js';
import { useStickyQuery } from '../use-sticky-query.js';

/**
 * The open questions of one request, answerable where the request is worked on.
 *
 * This replaces the read-only "Angebot unvollständig" box that used to sit here. It listed
 * exactly these facts and offered nothing to do about them — the answer had to be given in a
 * separate tab, on a question torn out of the position it is about. A question about position
 * 20 is not answerable without seeing position 20.
 *
 * Everything is answered in one pass and submitted together: one revalidation, one sync, one
 * hand-off to SAP at the end, instead of one of each per question.
 */

/** Blocking first, then what the customer has to settle, then what was merely assumed. */
const GROUPS = [
  {
    key: 'blocking',
    title: 'Blockiert die Übermittlung an SAP',
    hint: 'Ohne diese Angaben kann das Angebot nicht übermittelt werden.',
    accent: 'border-error/30 bg-error/5',
    icon: 'error',
    iconClass: 'text-error/70',
    categories: [NotificationCategory.SAP_VALIDATION, NotificationCategory.TECHNICAL_ERROR],
  },
  {
    key: 'customer',
    title: 'Beim Kunden zu erfragen',
    hint: 'Die Anfrage selbst lässt das offen — wir können es nicht aus ihr ableiten.',
    accent: 'border-warning/30 bg-warning/5',
    icon: 'help',
    iconClass: 'text-warning/80',
    categories: [NotificationCategory.CUSTOMER_INFO_MISSING],
  },
  {
    key: 'assumption',
    title: 'Angenommen — bitte kurz prüfen',
    hint: 'Die Extraktion war sich nicht sicher und hat vorläufig entschieden.',
    accent: 'border-info/30 bg-info/5',
    icon: 'lightbulb',
    iconClass: 'text-info/80',
    categories: [NotificationCategory.AI_REVIEW],
  },
] as const;

const SELECTION_SET = {
  _id: 1,
  category: 1,
  severity: 1,
  status: 1,
  title: 1,
  body: 1,
  positionNumber: 1,
  // Both read by `referenceKindOf` to decide whether this question wants a master record.
  fieldKey: 1,
  productId: 1,
  allowsFreeText: 1,
  options: { _id: 1, key: 1, label: 1, confirms: 1 },
  answer: {
    _id: 1,
    selectedOptionKey: 1,
    freeText: 1,
    answeredAt: 1,
    outcomeSummary: 1,
    rejections: 1,
    interpretation: { _id: 1, summary: 1, confidence: 1, needsConfirmation: 1, applied: 1 },
  },
} as const;

const optionKeyField = (id: string) => `option:${id}`;
const freeTextField = (id: string) => `text:${id}`;

/** What was picked from the master data, and how to show it back. */
interface PickedReference {
  value: string;
  label: string;
}

const COMPANY_SELECTION = { _id: 1, debitor: 1, name: 1, address: 1, city: 1 } as const;
const PRODUCT_SELECTION = {
  _id: 1,
  materialNumber: 1,
  material: 1,
  productGroup: 1,
  outerDiameterOne: 1,
  outerDiameterTwo: 1,
  wallThickness: 1,
} as const;

const productDimensions = (product: Product) =>
  [product.outerDiameterOne, product.outerDiameterTwo, product.wallThickness].filter(Boolean).join(' x ');

/** A question whose only option confirms the assumption is one click, not a dropdown. */
const soleConfirmOption = (notification: UserNotification) => {
  const options = notification.options ?? [];
  const [only] = options;
  return options.length === 1 && only?.confirms ? only : null;
};

const pendingReading = (notification: UserNotification) =>
  notification.answer?.interpretation?.needsConfirmation ? notification.answer.interpretation : null;

function renderPending(
  notification: UserNotification,
  busy: boolean,
  onDecide: (accept: boolean) => void,
): TemplateResult {
  const reading = notification.answer?.interpretation;
  return html`
    <div class="mt-2 rounded-lg border border-info/40 bg-info/5 px-3 py-2">
      <div class="text-xs font-semibold text-info/90 flex items-center gap-1.5">
        <x-icon class="text-sm">psychology</x-icon>So haben wir Ihre Antwort verstanden
      </div>
      <div class="text-sm mt-1">${reading?.summary}</div>
      <div class="flex gap-2 mt-2">
        <button class="btn btn-xs btn-primary" ?disabled=${busy} @click=${() => onDecide(true)}>
          Stimmt, übernehmen
        </button>
        <button class="btn btn-xs btn-ghost" ?disabled=${busy} @click=${() => onDecide(false)}>
          Nein, anders
        </button>
      </div>
    </div>
  `;
}

/**
 * What the last answer did. Shown for an open question too: an answer that was refused is the
 * reason the question is still standing, and hiding it makes the question look untouched.
 */
function renderAnswered(notification: UserNotification): TemplateResult | typeof nothing {
  const answer = notification.answer;
  if (!answer || pendingReading(notification)) return nothing;
  const refused = answer.rejections?.length ? answer.rejections : null;
  const chosen = (notification.options ?? []).find(option => option.key === answer.selectedOptionKey);
  const said = chosen?.label ?? answer.freeText;
  if (!refused && notification.status !== NotificationStatus.ANSWERED) return nothing;

  return html`
    <div class="mt-2 text-xs flex items-start gap-1.5 ${refused ? 'text-error/80' : 'text-base-content/50'}">
      <x-icon class="text-sm ${refused ? 'text-error/70' : 'text-success/70'}">
        ${refused ? 'error_outline' : 'check_circle'}
      </x-icon>
      <span>
        <span class="block">Ihre Antwort: ${said}</span>
        ${answer.outcomeSummary ? html`<span class="block">${answer.outcomeSummary}</span>` : nothing}
        ${refused ? html`<span class="block">${refused.join(' ')}</span>` : nothing}
      </span>
    </div>
  `;
}

/**
 * What a question card needs from its component. Passed explicitly rather than closed over so
 * the rendering stays out of the component body — it is the bulk of this file, and the hook
 * order in `stateful` is easier to read when it is not buried under markup.
 */
interface QuestionContext {
  busy: boolean;
  field: (key: string) => FunctionalFormFieldProps<string>;
  picked: Record<string, PickedReference>;
  onPickReference: (notificationID: string, picked: PickedReference) => void;
  onClearReference: (notificationID: string) => void;
  onConfirm: (notificationID: string, optionKey: string) => void;
  onDecideReading: (notificationID: string, accept: boolean) => void;
}

/**
 * The master-data search for a question that asks for a record rather than a value.
 *
 * The same searches the review UI next door already offers — there was never a reason for the
 * question to be the one place where a customer has to be described in prose and then guessed
 * at. What leaves here is the debitor or the material number, so the answer cannot come back
 * ambiguous, and no model is involved in a value that was picked from a list.
 */
function renderReferenceSearch(
  notification: UserNotification,
  kind: ReferenceKind,
  ctx: QuestionContext,
): TemplateResult {
  const pick = (picked: PickedReference) => ctx.onPickReference(notification._id, picked);

  if (kind === 'company')
    return html`${DropdownSelectionInput<Company>({
      // The declared array type is an intention, not a guarantee — the dropdown must never be
      // handed undefined, whatever a query returns on a bad day.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      search: async (searchString: string) => (await searchCompanies(searchString)(COMPANY_SELECTION)) ?? [],
      renderString: (company: Company) => `${company.debitor ?? '–'} – ${company.name ?? '–'}`,
      renderItem: (company: Company) => html`
        <div class="py-1">
          <div class="font-medium">${company.debitor ?? '–'} – ${company.name ?? '–'}</div>
          <div class="text-xs text-base-content/50">
            ${[company.address, company.city].filter(Boolean).join(', ') || '–'}
          </div>
        </div>
      `,
      resolveID: (company: Company) => company._id,
      placeholder: 'Firma im Stammsatz suchen (Debitor, Name, Ort …)',
      disabled: ctx.busy,
      onPicked: (company: Company | null) => {
        if (!company?.debitor) return;
        pick({ value: company.debitor, label: `${company.debitor} – ${company.name ?? '–'}` });
      },
    })}`;

  return html`${DropdownSelectionInput<Product>({
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- see above.
    search: async (searchString: string) => (await searchProducts(searchString)(PRODUCT_SELECTION)) ?? [],
    renderString: (product: Product) => `${product.materialNumber ?? '–'} – ${product.material ?? '–'}`,
    renderItem: (product: Product) => html`
      <div class="py-1">
        <div class="font-medium">${product.materialNumber ?? '–'} – ${product.material ?? '–'}</div>
        <div class="text-xs text-base-content/50">
          ${product.productGroup ?? '–'}${productDimensions(product) ? ` · ${productDimensions(product)}` : ''}
        </div>
      </div>
    `,
    resolveID: (product: Product) => product._id,
    placeholder: 'Artikel im Stammsatz suchen (Mat.-Nr., Material, Abmessung …)',
    disabled: ctx.busy,
    onPicked: (product: Product | null) => {
      if (!product?.materialNumber) return;
      pick({ value: product.materialNumber, label: `${product.materialNumber} – ${product.material ?? '–'}` });
    },
  })}`;
}

/** The picker, or what it already yielded — with the way back to changing it. */
function renderReference(notification: UserNotification, kind: ReferenceKind, ctx: QuestionContext): TemplateResult {
  const chosen = ctx.picked[notification._id];
  if (!chosen) return renderReferenceSearch(notification, kind, ctx);
  return html`
    <div class="flex items-center gap-1.5 text-[12.5px]">
      <x-icon class="text-sm text-success/70">check_circle</x-icon>
      <span>Gewählt: <b>${chosen.label}</b></span>
      <button
        class="btn btn-ghost btn-xs"
        ?disabled=${ctx.busy}
        @click=${() => ctx.onClearReference(notification._id)}
      >
        ändern
      </button>
    </div>
  `;
}

function renderAnswerControls(notification: UserNotification, ctx: QuestionContext): TemplateResult {
  const confirmOption = soleConfirmOption(notification);
  const options = notification.options ?? [];
  const referenceKind = referenceKindOf(notification);
  return html`
    <div class="mt-2 pl-1 flex flex-col gap-2">
      ${referenceKind ? renderReference(notification, referenceKind, ctx) : nothing}
      ${confirmOption
        ? html`<div>
            <button
              class="btn btn-xs btn-outline"
              ?disabled=${ctx.busy}
              @click=${() => ctx.onConfirm(notification._id, confirmOption.key)}
            >
              <x-icon class="text-sm">check</x-icon>${confirmOption.label}
            </button>
          </div>`
        : nothing}
      ${options.length && !confirmOption
        ? DropdownSelectionInput<string>({
            ...ctx.field(optionKeyField(notification._id)),
            selectables: options.map(option => option.key),
            renderString: key => options.find(option => option.key === key)?.label ?? key,
            placeholder: 'Antwort wählen …',
            clearable: true,
            disabled: ctx.busy,
          })
        : nothing}
      ${notification.allowsFreeText
        ? TextareaInput({
            ...ctx.field(freeTextField(notification._id)),
            rows: 2,
            placeholder: confirmOption ? 'Nein — was ist richtig?' : 'Oder in eigenen Worten …',
            disabled: ctx.busy,
          })
        : nothing}
    </div>
  `;
}

function renderQuestionCard(notification: UserNotification, ctx: QuestionContext): TemplateResult {
  return html`
    <div class="border-t border-base-200/70 first:border-t-0 py-3 first:pt-0 last:pb-0">
      <div class="flex items-start gap-2">
        ${notification.positionNumber
          ? html`<span class="badge badge-sm badge-ghost shrink-0 mt-0.5">Pos. ${notification.positionNumber}</span>`
          : nothing}
        <div class="min-w-0 flex-1">
          <div class="text-sm font-medium">${notification.title}</div>
          <div class="text-xs text-base-content/55 whitespace-pre-line mt-0.5">${notification.body}</div>
        </div>
      </div>
      ${pendingReading(notification)
        ? renderPending(notification, ctx.busy, accept => ctx.onDecideReading(notification._id, accept))
        : renderAnswerControls(notification, ctx)}
      ${renderAnswered(notification)}
    </div>
  `;
}

function renderGroupCard(
  group: (typeof GROUPS)[number],
  members: UserNotification[],
  ctx: QuestionContext,
): TemplateResult {
  return html`
    <div class="rounded-xl border ${group.accent} px-4 py-3 mb-3">
      <div class="flex items-center gap-2">
        <x-icon class="text-base ${group.iconClass}">${group.icon}</x-icon>
        <span class="text-sm font-semibold">${group.title}</span>
        <span class="badge badge-sm badge-ghost">${members.length}</span>
      </div>
      <div class="text-xs text-base-content/50 mt-0.5 mb-2">${group.hint}</div>
      <div class="flex flex-col">${members.map(member => renderQuestionCard(member, ctx))}</div>
    </div>
  `;
}

/**
 * Fallback: notifications only exist when the forwarder is a registered internal user. For a
 * mail from anyone else the questions are never raised — without this the request view would
 * show nothing at all about the fields that block it, which is what the old read-only box was
 * there for.
 */
function renderFallbackErrors(fallbackErrors: SapFieldIssue[]): TemplateResult | typeof nothing {
  if (!fallbackErrors.length) return nothing;
  return html`
    <div class="rounded-xl bg-error/5 px-4 py-3 mb-4">
      <div class="flex items-center gap-2 text-sm font-medium text-error/90">
        <x-icon class="text-base">error</x-icon> Angebot unvollständig – Pflichtangaben fehlen
      </div>
      <ul class="mt-1.5 space-y-0.5 text-xs text-base-content/55">
        ${fallbackErrors.map(
          issue => html`<li class="flex items-start gap-1.5">
            <x-icon class="text-xs mt-0.5 text-error/60">close</x-icon><span>${issue.message}</span>
          </li>`,
        )}
      </ul>
    </div>
  `;
}

export const RequestQuestions = stateful(
  (props: {
    mailID: string;
    /** Shown when no notification exists — see the fallback note in the render below. */
    fallbackErrors: SapFieldIssue[];
    onAnswered?: () => void;
  }) => {
    const { mailID, fallbackErrors } = props;
    const [busy, setBusy] = useState(false);
    const [version, setVersion] = useState(0);
    // Deliberately outside the form document: `useForm` rebuilds that whenever the object it
    // was handed changes, so putting a pick in there would clear free text typed into the
    // neighbouring questions. Reset when the set of questions changes.
    const [picked, setPicked] = useState<Record<string, PickedReference>>({});

    // Sticky over the version bump: re-subscribing empties the list for a roundtrip, and an
    // empty list here is not "no questions pending", it is the fallback box for a request that
    // never had any — so submitting an answer made the whole block flash into something else.
    const { value: notifications } = useStickyQuery(
      () => subscribeRequestNotifications(mailID)(SELECTION_SET),
      [mailID, version],
      [mailID],
    );
    const list = notifications ?? [];

    // One flat draft over all questions, so a single form carries the whole pass. Keyed by
    // notification id: the set changes as questions are answered and closed.
    const draftKey = list.map(item => item._id).join(',');
    const [draft, setDraft] = useState<Record<string, string>>({});

    // Memoised, because `useForm` keys its watched document on the identity of the object it is
    // handed — not on the deps passed next to it. Handing it a fresh literal per render rebuilt
    // that document, empty, on every render; and the first thing that renders after a keystroke
    // is that keystroke, because picking a value re-renders the component. The typed answer was
    // therefore gone before `hasDraft` could see it, so "Antworten" stayed disabled and nothing
    // could be sent from here at all. Every other useForm in this app passes a stable reference.
    const formDraft = useMemo<Record<string, string>>(
      () => [
        {
          ...Object.fromEntries(
            list.flatMap((item): Array<[string, string]> => [
              [optionKeyField(item._id), ''],
              [freeTextField(item._id), ''],
            ]),
          ),
          ...draft,
        },
      ],
      [draftKey, draft],
    );
    const { document: answers, field } = useForm<Record<string, string>>(formDraft, { validator: undefined }, [
      draftKey,
      draft,
    ]);

    const finish = (message: string) => {
      void XSnackbar.show(message);
      setDraft({});
      setPicked({});
      setVersion(version + 1);
      props.onAnswered?.();
    };

    const fail = (err: Error) => {
      void XSnackbar.show('Antwort konnte nicht verarbeitet werden: ' + err.message);
    };

    const submitAll = () => {
      const payload = list
        .map(
          item =>
            new NotificationAnswerDraft({
              notificationID: item._id,
              optionKey: answers[optionKeyField(item._id)] || null,
              freeText: answers[freeTextField(item._id)]?.trim() || null,
              reference: picked[item._id]?.value ?? null,
            }),
        )
        .filter(item => item.optionKey ?? item.freeText ?? item.reference);
      if (!payload.length) {
        void XSnackbar.show('Bitte mindestens eine Frage beantworten.');
        return;
      }
      setBusy(true);
      void answerRequestNotifications(mailID, payload)()
        .then(finish)
        .catch(fail)
        .finally(() => setBusy(false));
    };

    const confirmOne = (notificationID: string, optionKey: string) => {
      setBusy(true);
      void answerNotification(notificationID, optionKey)()
        .then(finish)
        .catch(fail)
        .finally(() => setBusy(false));
    };

    const decideReading = (notificationID: string, accept: boolean) => {
      setBusy(true);
      void confirmAnswerInterpretation(notificationID, accept)()
        .then(finish)
        .catch(fail)
        .finally(() => setBusy(false));
    };

    const ctx: QuestionContext = {
      busy,
      field,
      picked,
      onPickReference: (notificationID, choice) => setPicked({ ...picked, [notificationID]: choice }),
      onClearReference: notificationID => {
        const { [notificationID]: _removed, ...rest } = picked;
        setPicked(rest);
      },
      onConfirm: confirmOne,
      onDecideReading: decideReading,
    };
    const renderGroup = (group: (typeof GROUPS)[number]) => {
      const members = list.filter(item => (group.categories as readonly string[]).includes(item.category));
      return members.length ? renderGroupCard(group, members, ctx) : nothing;
    };

    if (!list.length) return renderFallbackErrors(fallbackErrors);

    const hasDraft = list.some(
      item => answers[optionKeyField(item._id)] || answers[freeTextField(item._id)]?.trim() || picked[item._id],
    );

    return html`
      <div class="mb-4">
        ${GROUPS.map(renderGroup)}
        <div class="flex items-center gap-3">
          <button class="btn btn-primary btn-sm gap-2" ?disabled=${busy || !hasDraft} @click=${submitAll}>
            <x-icon class="text-base">send</x-icon>${busy ? 'Wird verarbeitet …' : 'Antworten'}
          </button>
          <span class="text-xs text-base-content/45">
            Alle Antworten werden gemeinsam übernommen — danach geht die Anfrage an SAP, sobald nichts mehr fehlt.
          </span>
        </div>
      </div>
    `;
  },
);
